import { supabase } from '@/integrations/supabase/client';
import { formatDateToString } from '@/lib/dateUtils';

export interface Doctor {
  id: string;
  name: string;
  short_name?: string; // Added optional short_name property
  specialization: string;
  color: string;
  status: 'active' | 'inactive';
  company?: string;
  priority: number;
  join_date?: string; // Added optional join_date property
  preferred_room_id?: string; // Added optional preferred_room_id property
  created_at: string;
  updated_at: string;
  clinic_id?: string;
}

export interface Staff {
  id: string;
  name: string;
  short_name?: string; // Added optional short_name property
  role: string;
  color: string;
  status: 'active' | 'inactive';
  priority: number; // Added priority field
  join_date?: string; // Added optional join_date property
  created_at: string;
  updated_at: string;
  clinic_id?: string;
}

export interface StaffForTasks extends Staff {
  clinic?: {
    id: string;
    name: string;
    code: string;
  } | null;
  clinicName: string;
  clinicCode: string;
  category: string;
}

export interface DentalProcedure {
  id: string;
  name: string;
  description: string;
  duration: number;
  estimated_duration: number;
  category: string;
  price: number;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  clinic_id?: string;
}

export interface AdminTask {
  id: string;
  name: string;
  description: string;
  duration: number;
  estimated_duration: number;
  category: string;
  priority: 'low' | 'medium' | 'high';
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  clinic_id?: string;
}

export interface Room {
  id: string;
  name: string;
  clinic?: string; // Keeping for backward compatibility, will be removed later
  clinic_id?: string;
  color?: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

export interface DoctorStaffPreference {
  id: string;
  doctor_id: string;
  staff_id: string;
  preference_rank: 1 | 2 | 3 | 4; // 1=Main Nurse, 2=Primary Backup, 3=Secondary Backup, 4=Third Backup
  created_at: string;
  updated_at: string;
}

export interface DoctorStaffPreferenceWithDetails extends DoctorStaffPreference {
  staff: Staff;
  doctor: Doctor;
}

export interface ScheduleItem {
  id: string;
  doctorId?: string;
  staffId?: string;
  roomId?: string;
  startTime: string;
  endTime: string;
  date: string;
  endDate?: string; // Added for recurring schedules
  type: 'appointment' | 'admin' | 'break' | 'leave' | 'mc';
  title: string;
  notes?: string;
  procedure?: string;
  adminTask?: string;
  created_at?: string;
  updated_at?: string;
  clinic_id?: string;
  external_source?: string; // External system source: 'plato', null for local
  external_id?: string; // ID from external system for cross-reference
}

// Doctor operations
export const getDoctors = async (clinicId?: string) => {
  let query = supabase
    .from('doctors')
    .select('*')
    .order('name');

  if (clinicId) {
    // Get doctors that are either assigned to this clinic or work across clinics (null clinic_id)
    query = query.or(`clinic_id.eq.${clinicId},clinic_id.is.null`);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data as Doctor[];
};

export const createDoctor = async (doctor: Omit<Doctor, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('doctors')
    .insert([doctor])
    .select()
    .single();

  if (error) throw error;
  return data as Doctor;
};

export const updateDoctor = async (id: string, updates: Partial<Doctor>) => {
  const { data, error } = await supabase
    .from('doctors')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Doctor;
};

export const deleteDoctor = async (id: string) => {
  const { error } = await supabase
    .from('doctors')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

// Staff operations
export const getStaff = async (clinicId?: string) => {
  let query = supabase
    .from('staff')
    .select('*')
    .order('name');

  if (clinicId) {
    // Get staff that are either assigned to this clinic or work across clinics (null clinic_id)
    query = query.or(`clinic_id.eq.${clinicId},clinic_id.is.null`);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data as (Staff & { is_doctor?: boolean | null })[];
};

export const getAllStaffForTasks = async (): Promise<StaffForTasks[]> => {
  const { data, error } = await supabase
    .from('staff')
    .select(`
      *,
      clinic:clinics(
        id,
        name,
        code
      )
    `)
    .eq('status', 'active')
    .order('name');

  if (error) throw error;

  return data?.map(staff => ({
    ...staff,
    clinicName: staff.clinic?.name || 'Office Staff',
    clinicCode: staff.clinic?.code || 'office',
    category: !staff.clinic_id ? 'Office Staff' :
              (staff.clinic?.code ?? '').toLowerCase() === 'forum' ? 'Forum' :
              (staff.clinic?.code ?? '').toLowerCase() === 'ngeeann' ? 'Ngee Ann' :
              staff.clinic?.name || 'Other'
  })) || [];
};

export const createStaff = async (staff: Omit<Staff, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('staff')
    .insert([staff])
    .select()
    .single();

  if (error) throw error;
  return data as Staff;
};

export const updateStaff = async (id: string, updates: Partial<Staff>) => {
  const { data, error } = await supabase
    .from('staff')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Staff;
};

export const deleteStaff = async (id: string) => {
  const { error } = await supabase
    .from('staff')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

// Get monthly preview with limited items per day
export const getMonthlySchedulePreview = async (
  clinicId: string,
  startDate: string,
  endDate: string,
  itemsPerDay: number = 8
) => {
  console.log('[getMonthlySchedulePreview] Fetching preview for clinic:', clinicId);
  console.log('[getMonthlySchedulePreview] Date range:', startDate, 'to', endDate);
  console.log('[getMonthlySchedulePreview] Items per day:', itemsPerDay);

  try {
    const { data, error } = await supabase.rpc('get_monthly_schedule_preview', {
      p_clinic_id: clinicId,
      p_start_date: startDate,
      p_end_date: endDate,
      p_items_per_day: itemsPerDay
    });

    if (error) {
      console.error('[getMonthlySchedulePreview] RPC Error:', error);
      console.error('[getMonthlySchedulePreview] Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      
      // If RPC fails, fall back to regular fetch with limit
      console.log('[getMonthlySchedulePreview] Falling back to regular fetch due to RPC error');
      return await getScheduleItemsWithLimit(clinicId, startDate, endDate, 900); // Safe limit within Supabase's 1000 cap
    }

    console.log('[getMonthlySchedulePreview] Fetched preview items count:', data?.length || 0);
    
    if (!data || data.length === 0) {
      console.log('[getMonthlySchedulePreview] No data returned from RPC, trying regular fetch');
      return await getScheduleItemsWithLimit(clinicId, startDate, endDate, 900); // Safe limit within Supabase's 1000 cap
    }

    // Map database fields to interface fields
    return data.map((item: any) => ({
      id: item.id,
      doctorId: item.doctor_id,
      staffId: item.staff_id,
      roomId: item.room_id,
      startTime: item.start_time,
      endTime: item.end_time,
      date: item.date,
      endDate: item.end_date,
      type: item.type,
      title: item.title,
      notes: item.notes,
      procedure: item.procedure,
      adminTask: item.admin_task,
      created_at: item.created_at,
      updated_at: item.updated_at,
      clinic_id: item.clinic_id,
      external_source: item.external_source,
      external_id: item.external_id,
    }));
  } catch (error) {
    console.error('[getMonthlySchedulePreview] Unexpected error:', error);
    console.log('[getMonthlySchedulePreview] Falling back to regular fetch');
    // Fall back to regular fetch if anything goes wrong
    return await getScheduleItemsWithLimit(clinicId, startDate, endDate, 900); // Safe limit within Supabase's 1000 cap
  }
};

// Smart monthly schedule loading with leave priority
export const getMonthlyScheduleSmart = async (
  clinicId: string,
  startDate: string,
  endDate: string,
  itemsPerDay: number = 8
) => {
  console.log('[getMonthlyScheduleSmart] Fetching smart monthly schedule for clinic:', clinicId);
  console.log('[getMonthlyScheduleSmart] Date range:', startDate, 'to', endDate);
  console.log('[getMonthlyScheduleSmart] Items per day limit:', itemsPerDay);

  try {
    // Try RPC function first
    const { data, error } = await supabase.rpc('get_monthly_schedule_preview' as any, {
      p_clinic_id: clinicId,
      p_start_date: startDate,
      p_end_date: endDate,
      p_items_per_day: itemsPerDay
    });

    if (error) {
      console.error('[getMonthlyScheduleSmart] RPC Error:', error);
      console.log('[getMonthlyScheduleSmart] Falling back to two-query approach');
      
      // Fallback: Two-query approach
      // 1. Get all leave items (no limit)
      const { data: leaveData, error: leaveError } = await supabase
        .from('schedule_items')
        .select('*')
        .eq('clinic_id', clinicId)
        .or(`and(date.gte.${startDate},date.lte.${endDate}),and(date.lte.${endDate},end_date.gte.${startDate})`)
        .in('type', ['leave', 'mc'])
        .order('date', { ascending: true })
        .order('start_time', { ascending: true });

      if (leaveError) {
        console.error('[getMonthlyScheduleSmart] Leave fetch error:', leaveError);
        throw leaveError;
      }

      // 2. Get regular items - fetch more than needed, then limit per day
      const maxRegularItems = 500; // Fetch more to ensure we cover all days
      const { data: regularData, error: regularError } = await supabase
        .from('schedule_items')
        .select('*')
        .eq('clinic_id', clinicId)
        .or(`and(date.gte.${startDate},date.lte.${endDate}),and(date.lte.${endDate},end_date.gte.${startDate})`)
        .not('type', 'in', '(leave,mc)')
        .order('date', { ascending: true })
        .order('start_time', { ascending: true })
        .limit(maxRegularItems);

      if (regularError) {
        console.error('[getMonthlyScheduleSmart] Regular fetch error:', regularError);
        throw regularError;
      }

      // Group regular items by date and limit to itemsPerDay per day
      const regularByDate: Record<string, any[]> = {};
      regularData?.forEach(item => {
        if (!regularByDate[item.date]) {
          regularByDate[item.date] = [];
        }
        // Only take first itemsPerDay items per day
        if (regularByDate[item.date].length < itemsPerDay) {
          regularByDate[item.date].push(item);
        }
      });

      // Flatten back to array
      const limitedRegularData = Object.values(regularByDate).flat();

      // Combine leave data with limited regular data
      const combinedData = [...(leaveData || []), ...limitedRegularData];
      combinedData.sort((a, b) => {
        // First sort by date
        if (a.date !== b.date) {
          return a.date.localeCompare(b.date);
        }
        // Then prioritize leaves
        const aIsLeave = ['leave', 'mc'].includes(a.type);
        const bIsLeave = ['leave', 'mc'].includes(b.type);
        if (aIsLeave && !bIsLeave) return -1;
        if (!aIsLeave && bIsLeave) return 1;
        // Finally sort by time
        return a.start_time.localeCompare(b.start_time);
      });

      console.log('[getMonthlyScheduleSmart] Fallback fetched:', combinedData.length, 'items');
      console.log('[getMonthlyScheduleSmart] Leave items:', leaveData?.length || 0);
      console.log('[getMonthlyScheduleSmart] Regular items fetched:', regularData?.length || 0);
      console.log('[getMonthlyScheduleSmart] Regular items after limiting:', limitedRegularData.length);
      console.log('[getMonthlyScheduleSmart] Days with data:', Object.keys(regularByDate).length);

      // Map and return
      return combinedData.map(item => ({
        id: item.id,
        doctorId: item.doctor_id,
        staffId: item.staff_id,
        roomId: item.room_id,
        startTime: item.start_time,
        endTime: item.end_time,
        date: item.date,
        endDate: item.end_date,
        type: item.type,
        title: item.title,
        notes: item.notes,
        procedure: item.procedure,
        adminTask: item.admin_task,
        created_at: item.created_at,
        updated_at: item.updated_at,
        clinic_id: item.clinic_id,
        external_source: item.external_source,
        external_id: item.external_id,
      }));
    }

    console.log('[getMonthlyScheduleSmart] RPC returned:', Array.isArray(data) ? data.length : 0, 'items');
    
    // Map database fields to interface fields
    return (Array.isArray(data) ? data : []).map((item: any) => ({
      id: item.id,
      doctorId: item.doctor_id,
      staffId: item.staff_id,
      roomId: item.room_id,
      startTime: item.start_time,
      endTime: item.end_time,
      date: item.date,
      endDate: item.end_date,
      type: item.type,
      title: item.title,
      notes: item.notes,
      procedure: item.procedure,
      adminTask: item.admin_task,
      created_at: item.created_at,
      updated_at: item.updated_at,
      clinic_id: item.clinic_id,
      external_source: item.external_source,
      external_id: item.external_id,
    }));
  } catch (error) {
    console.error('[getMonthlyScheduleSmart] Unexpected error:', error);
    // Don't fall back to regular getScheduleItems to avoid 900 limit warning
    throw error;
  }
};

// Helper function to get schedule items with a specific limit
const getScheduleItemsWithLimit = async (
  clinicId: string,
  startDate: string,
  endDate: string,
  limit: number
) => {
  console.log('[getScheduleItemsWithLimit] Fetching with limit:', limit);
  
  const { data, error } = await supabase
    .from('schedule_items')
    .select('*')
    .eq('clinic_id', clinicId)
    .or(`and(date.gte.${startDate},date.lte.${endDate}),and(date.lte.${endDate},end_date.gte.${startDate})`)
    .order('date', { ascending: true })
    .order('start_time', { ascending: true })
    .limit(limit);

  if (error) {
    console.error('[getScheduleItemsWithLimit] Error:', error);
    throw error;
  }

  console.log('[getScheduleItemsWithLimit] Fetched items:', data?.length || 0);

  // Map database fields to interface fields
  return (data || []).map(item => ({
    id: item.id,
    doctorId: item.doctor_id,
    staffId: item.staff_id,
    roomId: item.room_id,
    startTime: item.start_time,
    endTime: item.end_time,
    date: item.date,
    endDate: item.end_date,
    type: item.type,
    title: item.title,
    notes: item.notes,
    procedure: item.procedure,
    adminTask: item.admin_task,
    created_at: item.created_at,
    updated_at: item.updated_at,
    clinic_id: item.clinic_id,
    external_source: item.external_source,
    external_id: item.external_id,
  }));
};

// Get complete monthly schedule with sequential batch loading (no limits)
export const getMonthlyScheduleComplete = async (
  clinicId: string,
  startDate: string,
  endDate: string
): Promise<ScheduleItem[]> => {
  console.log('[getMonthlyScheduleComplete] Starting complete load for month');
  console.log('[getMonthlyScheduleComplete] Date range:', startDate, 'to', endDate);
  
  const allItems: any[] = [];
  const batchSize = 1000;
  let offset = 0;
  let batchNumber = 1;
  
  // Keep loading until we get less than a full batch
  while (true) {
    console.log(`[getMonthlyScheduleComplete] Loading batch ${batchNumber} (offset: ${offset})`);
    
    const { data, error } = await supabase
      .from('schedule_items')
      .select('*')
      .eq('clinic_id', clinicId)
      .or(`and(date.gte.${startDate},date.lte.${endDate}),and(date.lte.${endDate},end_date.gte.${startDate})`)
      .order('date', { ascending: true })
      .order('start_time', { ascending: true })
      .order('id', { ascending: true })
      .range(offset, offset + batchSize - 1);
    
    if (error) {
      console.error('[getMonthlyScheduleComplete] Error in batch', batchNumber, error);
      throw error;
    }
    
    if (data && data.length > 0) {
      console.log(`[getMonthlyScheduleComplete] Batch ${batchNumber} loaded: ${data.length} items`);
      allItems.push(...data);
      
      // If we got less than a full batch, we've loaded everything
      if (data.length < batchSize) {
        console.log('[getMonthlyScheduleComplete] All items loaded:', allItems.length);
        break;
      }
      
      offset += batchSize;
      batchNumber++;
      
      // Safety check to prevent infinite loops (max 10 batches = 10,000 items)
      if (batchNumber > 10) {
        console.warn('[getMonthlyScheduleComplete] Safety limit reached, stopping at 10 batches');
        break;
      }
    } else {
      // No more data
      console.log('[getMonthlyScheduleComplete] No more data to load');
      break;
    }
  }
  
  // Apply the smart sorting (leaves/MC first)
  allItems.sort((a, b) => {
    // First sort by date
    if (a.date !== b.date) {
      return a.date.localeCompare(b.date);
    }
    
    // Then prioritize leaves/MC
    const aIsLeave = ['leave', 'mc'].includes(a.type);
    const bIsLeave = ['leave', 'mc'].includes(b.type);
    
    // MC has higher priority than regular leave
    if (aIsLeave && bIsLeave) {
      if (a.type === 'mc' && b.type !== 'mc') return -1;
      if (a.type !== 'mc' && b.type === 'mc') return 1;
    }
    
    if (aIsLeave && !bIsLeave) return -1;
    if (!aIsLeave && bIsLeave) return 1;
    
    // Finally sort by time
    return a.start_time.localeCompare(b.start_time);
  });
  
  console.log('[getMonthlyScheduleComplete] Final sorted items:', allItems.length);
  
  // Map database fields to interface fields
  return allItems.map(item => ({
    id: item.id,
    doctorId: item.doctor_id,
    staffId: item.staff_id,
    roomId: item.room_id,
    startTime: item.start_time,
    endTime: item.end_time,
    date: item.date,
    endDate: item.end_date,
    type: item.type,
    title: item.title,
    notes: item.notes,
    procedure: item.procedure,
    adminTask: item.admin_task,
    created_at: item.created_at,
    updated_at: item.updated_at,
    clinic_id: item.clinic_id,
    external_source: item.external_source,
    external_id: item.external_id,
  }));
};

// Get all schedule items for a specific day
export const getScheduleItemsForDay = async (
  clinicId: string,
  date: string
) => {
  console.log('[getScheduleItemsForDay] Fetching all items for date:', date);

  const { data, error } = await supabase
    .from('schedule_items')
    .select('*')
    .eq('clinic_id', clinicId)
    .or(`date.eq.${date},and(date.lte.${date},end_date.gte.${date})`)
    .order('start_time', { ascending: true });

  if (error) {
    console.error('[getScheduleItemsForDay] Error fetching day items:', error);
    throw error;
  }

  console.log('[getScheduleItemsForDay] Fetched items count:', data?.length || 0);

  // Map database fields to interface fields
  return (data || []).map(item => ({
    id: item.id,
    doctorId: item.doctor_id,
    staffId: item.staff_id,
    roomId: item.room_id,
    startTime: item.start_time,
    endTime: item.end_time,
    date: item.date,
    endDate: item.end_date,
    type: item.type,
    title: item.title,
    notes: item.notes,
    procedure: item.procedure,
    adminTask: item.admin_task,
    created_at: item.created_at,
    updated_at: item.updated_at,
    clinic_id: item.clinic_id,
    external_source: item.external_source,
    external_id: item.external_id,
  }));
};

// Schedule Item operations with endDate support
export const getScheduleItems = async (
  clinicId?: string, 
  customStartDate?: string, 
  customEndDate?: string,
  viewType?: string
) => {
  console.log('[getScheduleItems] Fetching schedule items for clinic:', clinicId);
  console.log('[getScheduleItems] View type:', viewType);
  
  // Use custom dates if provided, otherwise calculate default date range
  let startDate: string;
  let endDate: string;
  
  if (customStartDate && customEndDate) {
    startDate = customStartDate;
    endDate = customEndDate;
    console.log('[getScheduleItems] Using custom date range');
  } else {
    // Default: last 7 days to next 21 days (28 days total)
    const today = new Date();
    const defaultStart = new Date(today);
    defaultStart.setDate(defaultStart.getDate() - 7);
    const defaultEnd = new Date(today);
    defaultEnd.setDate(defaultEnd.getDate() + 21);
    
    startDate = formatDateToString(defaultStart);
    endDate = formatDateToString(defaultEnd);
    console.log('[getScheduleItems] Using default date range (28 days total)');
  }
  
  // Use batch loading to fetch all items
  const BATCH_SIZE = 500; // Safe batch size to stay within Supabase limits
  const MAX_BATCHES = 20; // Safety limit to prevent infinite loops (10,000 items max)

  let allData: any[] = [];
  let batchNumber = 0;
  let hasMore = true;

  console.log('[getScheduleItems] Starting batch loading with batch size:', BATCH_SIZE);

  while (hasMore && batchNumber < MAX_BATCHES) {
    batchNumber++;
    const rangeStart = (batchNumber - 1) * BATCH_SIZE;
    const rangeEnd = rangeStart + BATCH_SIZE - 1;

    let query = supabase
      .from('schedule_items')
      .select('*');

    // Apply clinic filter BEFORE other conditions
    if (clinicId) {
      query = query.eq('clinic_id', clinicId);
    }

    // Include both single-day schedules and multi-day schedules that overlap with the date range
    // Using proper PostgREST syntax for OR conditions with complex logic
    query = query.or(
      `and(date.gte.${startDate},date.lte.${endDate}),and(date.lte.${endDate},end_date.gte.${startDate})`
    );

    // Apply ordering and pagination
    query = query
      .order('date', { ascending: true })
      .order('start_time', { ascending: true })
      .order('id', { ascending: true })
      .range(rangeStart, rangeEnd);

    const { data, error } = await query;

    if (error) {
      console.error('[getScheduleItems] Error fetching batch', batchNumber, ':', error);
      throw error;
    }

    const batchSize = data?.length || 0;
    console.log(`[getScheduleItems] Batch ${batchNumber}: fetched ${batchSize} items (range ${rangeStart}-${rangeEnd})`);

    if (data && data.length > 0) {
      allData = [...allData, ...data];
    }

    // If we got fewer items than the batch size, we've reached the end
    hasMore = batchSize === BATCH_SIZE;
  }

  if (batchNumber >= MAX_BATCHES) {
    console.warn(`[getScheduleItems] Reached maximum batch limit (${MAX_BATCHES}). Some items may not be loaded.`);
  }

  console.log(`[getScheduleItems] Batch loading complete: ${allData.length} total items across ${batchNumber} batch(es)`);
  console.log('[getScheduleItems] Date range:', startDate, 'to', endDate);

  // Log the first and last items
  if (allData.length > 0) {
    console.log('[getScheduleItems] First item date:', allData[0].date);
    console.log('[getScheduleItems] Last item date:', allData[allData.length - 1].date);
  }

  // Map database fields to interface fields
  return allData.map(item => ({
    id: item.id,
    doctorId: item.doctor_id,
    staffId: item.staff_id,
    roomId: item.room_id,
    startTime: item.start_time,
    endTime: item.end_time,
    date: item.date,
    endDate: item.end_date, // Map the new endDate field
    type: item.type,
    title: item.title,
    notes: item.notes,
    procedure: item.procedure,
    adminTask: item.admin_task,
    created_at: item.created_at,
    updated_at: item.updated_at,
    clinic_id: item.clinic_id,
    external_source: item.external_source,
    external_id: item.external_id,
  })) as ScheduleItem[];
};

export const createScheduleItem = async (scheduleItem: Omit<ScheduleItem, 'id' | 'created_at' | 'updated_at'>) => {
  console.log('[createScheduleItem] Creating schedule item:', scheduleItem);
  
  // Map interface fields to database fields
  const dbItem = {
    doctor_id: scheduleItem.doctorId,
    staff_id: scheduleItem.staffId,
    room_id: scheduleItem.roomId,
    start_time: scheduleItem.startTime,
    end_time: scheduleItem.endTime,
    date: scheduleItem.date,
    end_date: scheduleItem.endDate, // Map the new endDate field
    type: scheduleItem.type,
    title: scheduleItem.title,
    notes: scheduleItem.notes,
    procedure: scheduleItem.procedure,
    admin_task: scheduleItem.adminTask,
    clinic_id: scheduleItem.clinic_id,
    external_source: scheduleItem.external_source,
    external_id: scheduleItem.external_id,
  };
  
  console.log('[createScheduleItem] Database item to insert:', dbItem);

  const { data, error } = await supabase
    .from('schedule_items')
    .insert([dbItem])
    .select()
    .single();

  if (error) {
    console.error('[createScheduleItem] Error inserting schedule item:', error);
    throw error;
  }
  
  console.log('[createScheduleItem] Successfully created schedule item:', data);

  // Map back to interface format
  return {
    id: data.id,
    doctorId: data.doctor_id,
    staffId: data.staff_id,
    roomId: data.room_id,
    startTime: data.start_time,
    endTime: data.end_time,
    date: data.date,
    endDate: data.end_date, // Map the new endDate field
    type: data.type,
    title: data.title,
    notes: data.notes,
    procedure: data.procedure,
    adminTask: data.admin_task,
    created_at: data.created_at,
    updated_at: data.updated_at,
    clinic_id: data.clinic_id,
    external_source: data.external_source,
    external_id: data.external_id,
  } as ScheduleItem;
};

export const updateScheduleItem = async (id: string, updates: Partial<ScheduleItem>) => {
  // Extract base UUID from composite IDs (removes _day_X suffix) as a safety check
  const baseId = id.split('_day_')[0];
  
  // Map interface fields to database fields
  const dbUpdates: {
    doctor_id?: string;
    staff_id?: string;
    room_id?: string;
    start_time?: string;
    end_time?: string;
    date?: string;
    end_date?: string;
    type?: 'appointment' | 'admin' | 'break' | 'leave' | 'mc';
    title?: string;
    notes?: string;
    procedure?: string;
    admin_task?: string;
    clinic_id?: string;
  } = {};
  if (updates.doctorId !== undefined) dbUpdates.doctor_id = updates.doctorId;
  if (updates.staffId !== undefined) dbUpdates.staff_id = updates.staffId;
  if (updates.roomId !== undefined) dbUpdates.room_id = updates.roomId;
  if (updates.startTime !== undefined) dbUpdates.start_time = updates.startTime;
  if (updates.endTime !== undefined) dbUpdates.end_time = updates.endTime;
  if (updates.date !== undefined) dbUpdates.date = updates.date;
  if (updates.endDate !== undefined) dbUpdates.end_date = updates.endDate; // Map the new endDate field
  if (updates.type !== undefined) dbUpdates.type = updates.type;
  if (updates.title !== undefined) dbUpdates.title = updates.title;
  if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
  if (updates.procedure !== undefined) dbUpdates.procedure = updates.procedure;
  if (updates.adminTask !== undefined) dbUpdates.admin_task = updates.adminTask;
  if (updates.clinic_id !== undefined) dbUpdates.clinic_id = updates.clinic_id;

  const { data, error } = await supabase
    .from('schedule_items')
    .update(dbUpdates)
    .eq('id', baseId)
    .select()
    .single();

  if (error) throw error;

  // Map back to interface format
  return {
    id: data.id,
    doctorId: data.doctor_id,
    staffId: data.staff_id,
    roomId: data.room_id,
    startTime: data.start_time,
    endTime: data.end_time,
    date: data.date,
    endDate: data.end_date, // Map the new endDate field
    type: data.type,
    title: data.title,
    notes: data.notes,
    procedure: data.procedure,
    adminTask: data.admin_task,
    created_at: data.created_at,
    updated_at: data.updated_at,
    clinic_id: data.clinic_id,
    external_source: data.external_source,
    external_id: data.external_id,
  } as ScheduleItem;
};

export const deleteScheduleItem = async (id: string) => {
  // Extract base UUID from composite IDs (removes _day_X suffix) as a safety check
  const baseId = id.split('_day_')[0];
  
  const { error } = await supabase
    .from('schedule_items')
    .delete()
    .eq('id', baseId);

  if (error) throw error;
};

// Delete many schedule rows in one request (used to remove a whole multi-day leave).
export const deleteScheduleItems = async (ids: string[]) => {
  const baseIds = Array.from(new Set(ids.map((id) => id.split('_day_')[0]))).filter(Boolean);
  if (baseIds.length === 0) return;

  const { error } = await supabase
    .from('schedule_items')
    .delete()
    .in('id', baseIds);

  if (error) throw error;
};

// Dental procedure operations
export const getDentalProcedures = async (clinicId?: string) => {
  let query = supabase
    .from('dental_procedures')
    .select('*')
    .order('name');

  if (clinicId) {
    // Get procedures that are assigned to this clinic only
    query = query.eq('clinic_id', clinicId);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data as DentalProcedure[];
};

export const createDentalProcedure = async (procedure: Omit<DentalProcedure, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('dental_procedures')
    .insert([procedure])
    .select()
    .single();

  if (error) throw error;
  return data as DentalProcedure;
};

export const updateDentalProcedure = async (id: string, updates: Partial<DentalProcedure>) => {
  const { data, error } = await supabase
    .from('dental_procedures')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as DentalProcedure;
};

export const deleteDentalProcedure = async (id: string) => {
  const { error } = await supabase
    .from('dental_procedures')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

// Admin task operations
export const getAdminTasks = async (clinicId?: string) => {
  let query = supabase
    .from('admin_tasks')
    .select('*')
    .order('name');

  if (clinicId) {
    // Get admin tasks that are assigned to this clinic only
    query = query.eq('clinic_id', clinicId);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data as AdminTask[];
};

export const createAdminTask = async (task: Omit<AdminTask, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('admin_tasks')
    .insert([task])
    .select()
    .single();

  if (error) throw error;
  return data as AdminTask;
};

export const updateAdminTask = async (id: string, updates: Partial<AdminTask>) => {
  const { data, error } = await supabase
    .from('admin_tasks')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as AdminTask;
};

export const deleteAdminTask = async (id: string) => {
  const { error } = await supabase
    .from('admin_tasks')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

// Room operations
export const getRooms = async (clinicId?: string) => {
  let query = supabase
    .from('rooms')
    .select('*')
    .order('name');
  
  if (clinicId) {
    query = query.eq('clinic_id', clinicId);
  }
  
  const { data, error } = await query;

  if (error) throw error;
  return data as Room[];
};

export const createRoom = async (room: Omit<Room, 'id' | 'created_at' | 'updated_at'>) => {
  const roomData = {
    ...room,
    clinic: room.clinic || room.clinic_id || '',
  };
  const { data, error } = await supabase
    .from('rooms')
    .insert([roomData])
    .select()
    .single();

  if (error) throw error;
  return data as Room;
};

export const updateRoom = async (id: string, updates: Partial<Room>) => {
  const { data, error } = await supabase
    .from('rooms')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Room;
};

export const deleteRoom = async (id: string) => {
  const { error } = await supabase
    .from('rooms')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

// Doctor Staff Preferences operations
export const getDoctorPreferences = async (doctorId: string): Promise<DoctorStaffPreferenceWithDetails[]> => {
  const { data, error } = await supabase
    .from('doctor_staff_preferences')
    .select(`
      *,
      staff:staff_id!inner (
        id,
        name,
        short_name,
        role,
        status,
        priority
      )
    `)
    .eq('doctor_id', doctorId)
    .order('preference_rank');

  if (error) throw error;
  return data as DoctorStaffPreferenceWithDetails[];
};

export const getAllDoctorPreferences = async (): Promise<DoctorStaffPreferenceWithDetails[]> => {
  const { data, error } = await supabase
    .from('doctor_staff_preferences')
    .select(`
      *,
      staff:staff_id!inner (
        id,
        name,
        short_name,
        role,
        status,
        priority
      ),
      doctor:doctor_id!inner (
        id,
        name,
        short_name,
        specialization,
        status
      )
    `)
    .order('doctor_id, preference_rank');

  if (error) throw error;
  return data as DoctorStaffPreferenceWithDetails[];
};

export const updateDoctorPreferences = async (
  doctorId: string, 
  preferences: Array<{ staff_id: string; preference_rank: 1 | 2 | 3 | 4 }>
): Promise<void> => {
  // First, delete existing preferences for this doctor
  const { error: deleteError } = await supabase
    .from('doctor_staff_preferences')
    .delete()
    .eq('doctor_id', doctorId);

  if (deleteError) throw deleteError;

  // Then insert new preferences if any
  if (preferences.length > 0) {
    const { error: insertError } = await supabase
      .from('doctor_staff_preferences')
      .insert(
        preferences.map(pref => ({
          doctor_id: doctorId,
          staff_id: pref.staff_id,
          preference_rank: pref.preference_rank
        }))
      );

    if (insertError) throw insertError;
  }
};

export const getStaffPreferences = async (staffId: string): Promise<DoctorStaffPreferenceWithDetails[]> => {
  const { data, error } = await supabase
    .from('doctor_staff_preferences')
    .select(`
      *,
      doctor:doctor_id (
        id,
        name,
        short_name,
        specialization,
        status
      )
    `)
    .eq('staff_id', staffId)
    .order('preference_rank');

  if (error) throw error;
  return data as DoctorStaffPreferenceWithDetails[];
};

export const deleteDoctorPreference = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('doctor_staff_preferences')
    .delete()
    .eq('id', id);

  if (error) throw error;
};
